import React, { useState, useEffect } from "react";
import { Navbar, Nav } from "react-bootstrap";
import { FaBars, FaTimes, FaChevronDown, FaArrowRight, FaArrowLeft } from "react-icons/fa";
import logo from "../assets/freakslogo.png";
import { Link, useLocation } from "react-router-dom";
import LanguageToggle from "./LanguageToggle";
import { useLanguage } from "../contexts/LanguageContext";

function Header() {
  const location = useLocation();
  const { isArabic } = useLanguage();
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [activeSubItem, setActiveSubItem] = useState("Day Pass");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1400);
  useEffect(() => {
    const handleScroll = () => {
      const navbar = document.querySelector('.navbar-custom');
      if (window.scrollY > 125) {
        navbar?.classList.add('scrolled');
      } else {
        navbar?.classList.remove('scrolled');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const translations = {
    home: isArabic ? 'الرئيسية' : 'Home',
    freakyard: isArabic ? 'فريكيارد' : 'Freakyard',
    aboutUs: isArabic ? 'من نحن' : 'About Us',
    freakSquad: isArabic ? 'فريق الفريكس' : 'Freak Squad',
    timeline: isArabic ? 'الخط الزمني' : 'Timeline',
    partnerWithUs: isArabic ? 'كن شريكًا لنا' : 'Partner With Us',
    blog: isArabic ? 'المدونة' : 'Blog',
    joinNextEvent: isArabic ? 'احصل على تذاكرك الآن' : 'Get your Tickets Now',
    // joinNextEvent: isArabic ? 'انضم إلى الحدث القادم' : 'Join Next Event',

    followUs: isArabic ? 'تابعنا' : 'Follow Us',
    // Freakyard dropdown items
    lineup: isArabic ? 'الـ لاين اب' : 'Lineup',
    tickets: isArabic ? 'التذاكر' : 'Tickets',
    affiliate: isArabic ? 'برنامج الشراكة' : 'Affiliate',
    faq: isArabic ? 'الأسئلة الشائعة' : 'FAQ',
    // Dropdown descriptions
    aboutUsDesc: isArabic ? 'تعرّف علينا عن قرب: من نحن، ماذا نمثّل، وكيف وُلدت فريكس أوف نيتشر.' : 'Get to know who we are, what we stand for, and how Freaks of Nature came to life.',
    freakSquadDesc: isArabic ? 'تعرّف على الشخصيات التي تجسّد اختلاف الطبائع والطاقة داخل مجتمعنا.' : 'Meet the characters that represent the different personalities and energies within our community.',
    timelineDesc: isArabic ? 'استكشف أبرز اللحظات والإصدارات والعلامات الفارقة التي شكّلت فريكس أوف نيتشر.' : 'Explore the key moments, editions, and milestones that shaped Freaks of Nature.',
    partnerWithUsDesc: isArabic ? 'اكتشف كيف يمكنك التعاون مع فريكس أوف نيتشر عبر الشراكات، والاكتيفيشنز، والتجارب المشتركة.' : 'Learn how to collaborate with Freaks of Nature through partnerships, activations, and shared experiences.',

    lineupDesc: isArabic ? 'اكتشف الفنانين الذين ينسجون تجربة فريكيارد — عبر جميع المسارح، أنواع الموسيقى المختلفة، والتجارب الفريدة.' : 'Explore the artists shaping Freakyard — across all stages, sounds, and moments.',
    ticketsDesc: isArabic ? 'اختر نوع التذكرة المناسب لك، واحجز مكانك في فريكيارد قبل فتح البوابات.' : 'Choose your access and secure your entry to Freakyard before the gates open.',
    affiliateDesc: isArabic ? 'اكسب مكافآت من خلال مشاركة فريكيارد مع شبكتك عبر برنامج الأفلييت الرسمي.' : 'Earn rewards by sharing Freakyard with your network through our official affiliate program.',
    faqDesc: isArabic ? 'كل ما تحتاج معرفته — من الدخول والمواعيد إلى تفاصيل التجربة.' : 'Everything you need to know — from entry and timings to on-site experience.'
  };

  // Update isMobile state on window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1180);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close navbar on route change
  useEffect(() => {
    setIsNavOpen(false);
    setActiveDropdown(null);
  }, [location]);

  const toggleNav = () => {
    setIsNavOpen(!isNavOpen);
    if (isNavOpen) {
      setActiveDropdown(null);
    }
  };

  const toggleDropdown = (event, linkName) => {
    if (isMobile) {
      event.preventDefault();
      setActiveDropdown((prev) => (prev === linkName ? null : linkName));
    }
  };

  const handleDropdownLinkClick = (hash, event) => {
    setActiveDropdown(null);
    setIsNavOpen(false);

    const linkElement = event?.currentTarget?.closest('a');
    const href = linkElement?.getAttribute('href') || '';
    const targetHash = hash || (href.includes('#') ? '#' + href.split('#')[1] : '');

    const currentPath = window.location.pathname.replace(/\/$/, '');
    let targetPath = href.split('#')[0] || currentPath;
    if (targetPath.startsWith('/')) {
      targetPath = targetPath.replace(/\/$/, '');
    } else {
      targetPath = currentPath;
    }

    if (targetPath === currentPath && targetHash) {
      if (event) {
        event.preventDefault();
      }

      const hashValue = targetHash.replace('#', '');
      window.location.hash = hashValue;

      if (hashValue === "about-us") {
        setTimeout(() => {
          window.scrollTo({
            top: 0,
            behavior: 'smooth'
          });
        }, 150);
        return;
      }

      const getHeaderOffset = () => {
        const width = window.innerWidth;
        if (width <= 468) {
          return 164;
        } else if (width <= 880) {
          return 178;
        } else {
          return 124;
        }
      };

      if (hashValue === "tickets-multi-day-pass" || hashValue === "tickets-day-pass") {
        setTimeout(() => {
          const ticketsElement = document.getElementById("tickets");
          if (ticketsElement) {
            const headerOffset = getHeaderOffset();
            const elementPosition = ticketsElement.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - headerOffset;

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          } else {
            setTimeout(() => {
              const retryTicketsElement = document.getElementById("tickets");
              if (retryTicketsElement) {
                const headerOffset = getHeaderOffset();
                const elementPosition = retryTicketsElement.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition = elementPosition - headerOffset;

                window.scrollTo({
                  top: offsetPosition,
                  behavior: 'smooth'
                });
              }
            }, 300);
          }
        }, 150);
      } else {
        setTimeout(() => {
          const element = document.getElementById(hashValue);
          if (element) {
            const headerOffset = getHeaderOffset();
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - headerOffset;

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          } else {
            setTimeout(() => {
              const retryElement = document.getElementById(hashValue);
              if (retryElement) {
                const headerOffset = getHeaderOffset();
                const elementPosition = retryElement.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition = elementPosition - headerOffset;

                window.scrollTo({
                  top: offsetPosition,
                  behavior: 'smooth'
                });
              }
            }, 300);
          }
        }, 150);
      }
    }
  };

  const handleMouseEnter = (linkName) => {
    if (!isMobile) {
      setActiveDropdown(linkName);
    }
  };

  const handleLogoClick = () => {
    setActiveDropdown(null);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  const handleMainNavLinkClick = () => {
    setActiveDropdown(null);
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setActiveDropdown(null);
    }
    if (isMobile) {
      setIsNavOpen(false);
    }
  };

  // Navigation items in order based on language
  const navItems = isArabic ? (
    // Arabic order: Blog, Relive, Freakyard, About Us, Home
    <>
      {/* Blog Link */}
      <div className="nav-item-wrapper">
        <Nav.Link
          as={Link}
          to="/blog"
          className="nav-link nav-link-blog"
          onClick={() => {
            setActiveDropdown(null);
            setIsNavOpen(false);
            handleMainNavLinkClick();

            const getUTM = () => {
              const stored = sessionStorage.getItem('utm_params');
              return stored ? JSON.parse(stored) : {};
            };
            const utm = getUTM();

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              'event': 'action_cta_click',
              'button_text': 'Blog',
              'class_id': 'nav-link nav-link-blog',
              'position': {
                'page': 'home',
                'nav_bar': 'header',
                'sub_nav_bar': 'main-nav'
              },
              'referrer_source': document.referrer,
              'utm_source': utm.utm_source || '',
              'utm_medium': utm.utm_medium || '',
              'utm_campaign': utm.utm_campaign || '',
              'utm_term': utm.utm_term || '',
              'utm_content': utm.utm_content || '',
              'gclid': utm.gclid || '',
              'fbclid': utm.fbclid || '',
              'ttclid': utm.ttclid || ''
            });
          }}
        >
          {translations.blog}
        </Nav.Link>
      </div>

      <div className="nav-item-wrapper">
        <Nav.Link
          as={Link}
          to="/relive"
          className="nav-link nav-link-blog"
          onClick={() => {
            setActiveDropdown(null);
            setIsNavOpen(false);
            handleMainNavLinkClick();

            const getUTM = () => {
              const stored = sessionStorage.getItem('utm_params');
              return stored ? JSON.parse(stored) : {};
            };
            const utm = getUTM();

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              'event': 'action_cta_click',
              'button_text': 'Relive',
              'class_id': 'nav-link nav-link-relive',
              'position': {
                'page': 'home',
                'nav_bar': 'header',
                'sub_nav_bar': 'main-nav'
              },
              'referrer_source': document.referrer,
              'utm_source': utm.utm_source || '',
              'utm_medium': utm.utm_medium || '',
              'utm_campaign': utm.utm_campaign || '',
              'utm_term': utm.utm_term || '',
              'utm_content': utm.utm_content || '',
              'gclid': utm.gclid || '',
              'fbclid': utm.fbclid || '',
              'ttclid': utm.ttclid || ''
            });
          }}
        >
          Relive
        </Nav.Link>
      </div>

      {/* Freakyard Link */}
      <div
        className={`nav-item-wrapper ${activeDropdown === "Freakyard" ? "active" : ""
          }`}
        onMouseEnter={() => handleMouseEnter("Freakyard")}
        onMouseLeave={handleMouseLeave}
      >
        <Nav.Link
          as={isMobile ? "div" : Link}
          to={isMobile ? undefined : "/Event"}
          className={`nav-link nav-link-freakyard ${activeDropdown === "Freakyard" ? "active-link" : ""
            }`}
          onClick={(event) => {
            const getUTM = () => {
              const stored = sessionStorage.getItem('utm_params');
              return stored ? JSON.parse(stored) : {};
            };
            const utm = getUTM();

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              'event': 'action_cta_click',
              'button_text': 'Freakyard',
              'class_id': 'nav-link nav-link-freakyard',
              'position': {
                'page': 'home',
                'nav_bar': 'header',
                'sub_nav_bar': 'main-nav'
              },
              'referrer_source': document.referrer,
              'utm_source': utm.utm_source || '',
              'utm_medium': utm.utm_medium || '',
              'utm_campaign': utm.utm_campaign || '',
              'utm_term': utm.utm_term || '',
              'utm_content': utm.utm_content || '',
              'gclid': utm.gclid || '',
              'fbclid': utm.fbclid || '',
              'ttclid': utm.ttclid || ''
            });

            if (isMobile) {
              toggleDropdown(event, "Freakyard");
            } else {
              handleMainNavLinkClick();
            }
          }}
        >
          <span className="nav-link-label">{translations.freakyard}</span>
          <span className="dropdown-icon">
            <FaChevronDown />
          </span>
        </Nav.Link>
        {activeDropdown === "Freakyard" && (
          <div className="dropdown-freakyard">

            {/* Lineup Link */}
            <div
              className={`dropdown-item ${activeSubItem === "Lineup" ? "active" : ""
                }`}
              onMouseEnter={() => setActiveSubItem("Lineup")}
              onMouseLeave={() => setActiveSubItem(null)}
            >
              <Link
                to="/Event#lineup"
                className="dropdown-link"
                onClick={(e) => handleDropdownLinkClick("#lineup", e)}
              >
                {translations.lineup}
                {activeSubItem === "Lineup" && (
                  isArabic ? (
                    <FaArrowLeft className="left-arrow" />
                  ) : (
                    <FaArrowRight className="right-arrow" />
                  )
                )}
              </Link>

              {activeSubItem === "Lineup" && (
                <div className="dropdown-description lineup">
                  <p className="description-title">{translations.lineup}</p>
                  <p className="description-subtitle">
                    {translations.lineupDesc}
                  </p>
                </div>
              )}
            </div>

            {/* Tickets Link */}
            <div
              className={`dropdown-item ${activeSubItem === "Tickets" ? "active" : ""
                }`}
              onMouseEnter={() => setActiveSubItem("Tickets")}
              onMouseLeave={() => setActiveSubItem(null)}
            >
              <Link
                to="/Event#tickets"
                className="dropdown-link"
                onClick={(e) => handleDropdownLinkClick("#tickets", e)}
              >
                {translations.tickets}
                {activeSubItem === "Tickets" && (
                  isArabic ? (
                    <FaArrowLeft className="left-arrow" />
                  ) : (
                    <FaArrowRight className="right-arrow" />
                  )
                )}
              </Link>

              {activeSubItem === "Tickets" && (
                <div className="dropdown-description tickets">
                  <p className="description-title">{translations.tickets}</p>
                  <p className="description-subtitle">
                    {translations.ticketsDesc}
                  </p>
                </div>
              )}
            </div>

            {/* Affiliate Link */}
            <div
              className={`dropdown-item ${activeSubItem === "Affiliate" ? "active" : ""
                }`}
              onMouseEnter={() => setActiveSubItem("Affiliate")}
              onMouseLeave={() => setActiveSubItem(null)}
            >
              <Link
                to="/Event#affiliate"
                className="dropdown-link"
                onClick={(e) => handleDropdownLinkClick("#affiliate", e)}
              >
                {translations.affiliate}
                {activeSubItem === "Affiliate" && (
                  isArabic ? (
                    <FaArrowLeft className="left-arrow" />
                  ) : (
                    <FaArrowRight className="right-arrow" />
                  )
                )}
              </Link>

              {activeSubItem === "Affiliate" && (
                <div className="dropdown-description affiliate0">
                  <p className="description-title">{translations.affiliate}</p>
                  <p className="description-subtitle">
                    {translations.affiliateDesc}
                  </p>
                </div>
              )}
            </div>

            {/* FAQ Link */}
            <div
              className={`dropdown-item dropdown-item-faq ${activeSubItem === "FAQ" ? "active" : ""
                }`}
              onMouseEnter={() => setActiveSubItem("FAQ")}
              onMouseLeave={() => setActiveSubItem(null)}
            >
              <Link
                to="/Event#faq"
                className="dropdown-link"
                onClick={(e) => handleDropdownLinkClick("#faq", e)}
              >
                {translations.faq}
                {activeSubItem === "FAQ" && (
                  isArabic ? (
                    <FaArrowLeft className="left-arrow" />
                  ) : (
                    <FaArrowRight className="right-arrow" />
                  )
                )}
              </Link>

              {activeSubItem === "FAQ" && (
                <div className="dropdown-description faqs">
                  <p className="description-title">{translations.faq}</p>
                  <p className="description-subtitle">
                    {translations.faqDesc}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* About Us Link */}
      <div
        className={`nav-item-wrapper ${activeDropdown === "About Us" ? "active" : ""
          }`}
        onMouseEnter={() => handleMouseEnter("About Us")}
        onMouseLeave={handleMouseLeave}
      >
        <Nav.Link
          as={Link}
          to="/about"
          className={`nav-link nav-link-about ${activeDropdown === "About Us" ? "active-link" : ""
            }`}
          onClick={(event) => {
            const getUTM = () => {
              const stored = sessionStorage.getItem('utm_params');
              return stored ? JSON.parse(stored) : {};
            };
            const utm = getUTM();

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              'event': 'action_cta_click',
              'button_text': 'About Us',
              'class_id': 'nav-link nav-link-about',
              'position': {
                'page': 'home',
                'nav_bar': 'header',
                'sub_nav_bar': 'main-nav'
              },
              'referrer_source': document.referrer,
              'utm_source': utm.utm_source || '',
              'utm_medium': utm.utm_medium || '',
              'utm_campaign': utm.utm_campaign || '',
              'utm_term': utm.utm_term || '',
              'utm_content': utm.utm_content || '',
              'gclid': utm.gclid || '',
              'fbclid': utm.fbclid || '',
              'ttclid': utm.ttclid || ''
            });

            if (isMobile) {
              const clickedToggle = event.target.closest(".dropdown-icon");
              if (clickedToggle) {
                toggleDropdown(event, "About Us");
                return;
              }
              handleDropdownLinkClick("#about-us", event);
            } else {
              toggleDropdown(event, "About Us");
              handleMainNavLinkClick();
            }
          }}
        >
          <span className="nav-link-label">{translations.aboutUs}</span>
          <span className="dropdown-icon">
            <FaChevronDown />
          </span>
        </Nav.Link>
        {activeDropdown === "About Us" && (
          <div className="dropdown-about">
            <div
              className={`dropdown-item ${activeSubItem === "About Us" ? "active" : ""
                }`}
              onMouseEnter={() => setActiveSubItem("About Us")}
              onMouseLeave={() => setActiveSubItem(null)}
            >
              <Link
                to={isMobile ? "/about" : "/about#about-us"}
                className="dropdown-link dropdown-link-about-us"
                onClick={(e) => handleDropdownLinkClick("#about-us", e)}
              >
                {translations.aboutUs}
                {activeSubItem === "About Us" && (
                  isArabic ? (
                    <FaArrowLeft className="left-arrow" />
                  ) : (
                    <FaArrowRight className="right-arrow" />
                  )
                )}
              </Link>

              {activeSubItem === "About Us" && (
                <div className="dropdown-description ab">
                  <p className="description-title">{translations.aboutUs}</p>
                  <p className="description-subtitle">
                    {translations.aboutUsDesc}
                  </p>
                </div>
              )}
            </div>

            <div
              className={`dropdown-item ${activeSubItem === "Freak Squad" ? "active" : ""
                }`}
              onMouseEnter={() => setActiveSubItem("Freak Squad")}
              onMouseLeave={() => setActiveSubItem(null)}
            >
              <Link
                to="/about#freak-squad"
                className="dropdown-link dropdown-link-freak-squad"
                onClick={(e) => handleDropdownLinkClick("#freak-squad", e)}
              >
                {translations.freakSquad}
                {activeSubItem === "Freak Squad" && (
                  isArabic ? (
                    <FaArrowLeft className="left-arrow" />
                  ) : (
                    <FaArrowRight className="right-arrow" />
                  )
                )}
              </Link>

              {activeSubItem === "Freak Squad" && (
                <div className="dropdown-description freak-squad">
                  <p className="description-title">{translations.freakSquad}</p>
                  <p className="description-subtitle">
                    {translations.freakSquadDesc}
                  </p>
                </div>
              )}
            </div>

            <div
              className={`dropdown-item ${activeSubItem === "Timeline" ? "active" : ""
                }`}
              onMouseEnter={() => setActiveSubItem("Timeline")}
              onMouseLeave={() => setActiveSubItem(null)}
            >
              <Link
                to="/about#timeline"
                className="dropdown-link dropdown-link-timeline"
                onClick={(e) => handleDropdownLinkClick("#timeline", e)}
              >
                {translations.timeline}
                {activeSubItem === "Timeline" && (
                  isArabic ? (
                    <FaArrowLeft className="left-arrow" />
                  ) : (
                    <FaArrowRight className="right-arrow" />
                  )
                )}
              </Link>

              {activeSubItem === "Timeline" && (
                <div className="dropdown-description timeline">
                  <p className="description-title">{translations.timeline}</p>
                  <p className="description-subtitle">
                    {translations.timelineDesc}
                  </p>
                </div>
              )}
            </div>

            <div
              className={`dropdown-item dropdown-item-partner-with-us ${activeSubItem === "Partner With Us" ? "active" : ""
                }`}
              onMouseEnter={() => setActiveSubItem("Partner With Us")}
              onMouseLeave={() => setActiveSubItem(null)}
            >
              <Link
                to="/about#partner-with-us"
                className="dropdown-link dropdown-link-partner-with-us"
                onClick={(e) =>
                  handleDropdownLinkClick("#partner-with-us", e)
                }
              >
                {translations.partnerWithUs}
                {activeSubItem === "Partner With Us" && (
                  isArabic ? (
                    <FaArrowLeft className="left-arrow" />
                  ) : (
                    <FaArrowRight className="right-arrow" />
                  )
                )}
              </Link>

              {activeSubItem === "Partner With Us" && (
                <div className="dropdown-description partner">
                  <p className="description-title">{translations.partnerWithUs}</p>
                  <p className="description-subtitle">
                    {translations.partnerWithUsDesc}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Home Link */}
      <div className="nav-item-wrapper">
        <Nav.Link
          as={Link}
          to="/"
          className={`nav-link nav-link-home ${location.pathname === "/" ? "active-link" : ""}`}
          onClick={() => {
            setActiveDropdown(null);
            setIsNavOpen(false);
            handleMainNavLinkClick();
          }}
        >
          {translations.home}
        </Nav.Link>
      </div>
    </>
  ) : (
    // English order: Home, About Us, Blog
    <>
      {/* Home Link */}
      <div className="nav-item-wrapper">
        <Nav.Link
          as={Link}
          to="/"
          className={`nav-link nav-link-home ${location.pathname === "/" ? "active-link" : ""}`}
          onClick={() => {
            setActiveDropdown(null);
            setIsNavOpen(false);
            handleMainNavLinkClick();
          }}
        >
          {translations.home}
        </Nav.Link>
      </div>

      {/* Freakyard Link */}
      <div
        className={`nav-item-wrapper ${activeDropdown === "Freakyard" ? "active" : ""
          }`}
        onMouseEnter={() => handleMouseEnter("Freakyard")}
        onMouseLeave={handleMouseLeave}
      >
        <Nav.Link
          as={isMobile ? "div" : Link}
          to={isMobile ? undefined : "/Event"}
          className={`nav-link nav-link-freakyard ${activeDropdown === "Freakyard" ? "active-link" : ""
            }`}
          onClick={(event) => {
            const getUTM = () => {
              const stored = sessionStorage.getItem('utm_params');
              return stored ? JSON.parse(stored) : {};
            };
            const utm = getUTM();

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              'event': 'action_cta_click',
              'button_text': 'Freakyard',
              'class_id': 'nav-link nav-link-freakyard',
              'position': {
                'page': 'home',
                'nav_bar': 'header',
                'sub_nav_bar': 'main-nav'
              },
              'referrer_source': document.referrer,
              'utm_source': utm.utm_source || '',
              'utm_medium': utm.utm_medium || '',
              'utm_campaign': utm.utm_campaign || '',
              'utm_term': utm.utm_term || '',
              'utm_content': utm.utm_content || '',
              'gclid': utm.gclid || '',
              'fbclid': utm.fbclid || '',
              'ttclid': utm.ttclid || ''
            });

            if (isMobile) {
              toggleDropdown(event, "Freakyard");
            } else {
              handleMainNavLinkClick();
            }
          }}
        >
          <span className="nav-link-label">{translations.freakyard}</span>
          <span className="dropdown-icon">
            <FaChevronDown />
          </span>
        </Nav.Link>
        {activeDropdown === "Freakyard" && (
          <div className="dropdown-freakyard">
            {/* Lineup Link */}
            <div
              className={`dropdown-item ${activeSubItem === "Lineup" ? "active" : ""
                }`}
              onMouseEnter={() => setActiveSubItem("Lineup")}
              onMouseLeave={() => setActiveSubItem(null)}
            >
              <Link
                to="/Event#lineup"
                className="dropdown-link"
                onClick={(e) => handleDropdownLinkClick("#lineup", e)}
              >
                {translations.lineup}
                {activeSubItem === "Lineup" && (
                  <FaArrowRight className="right-arrow" />
                )}
              </Link>

              {activeSubItem === "Lineup" && (
                <div className="dropdown-description lineup">
                  <p className="description-title">{translations.lineup}</p>
                  <p className="description-subtitle">
                    {translations.lineupDesc}
                  </p>
                </div>
              )}
            </div>

            {/* Tickets Link */}
            <div
              className={`dropdown-item ${activeSubItem === "Tickets" ? "active" : ""
                }`}
              onMouseEnter={() => setActiveSubItem("Tickets")}
              onMouseLeave={() => setActiveSubItem(null)}
            >
              <Link
                to="/Event#tickets"
                className="dropdown-link"
                onClick={(e) => handleDropdownLinkClick("#tickets", e)}
              >
                {translations.tickets}
                {activeSubItem === "Tickets" && (
                  <FaArrowRight className="right-arrow" />
                )}
              </Link>

              {activeSubItem === "Tickets" && (
                <div className="dropdown-description tickets">
                  <p className="description-title">{translations.tickets}</p>
                  <p className="description-subtitle">
                    {translations.ticketsDesc}
                  </p>
                </div>
              )}
            </div>

            {/* Affiliate Link */}
            <div
              className={`dropdown-item ${activeSubItem === "Affiliate" ? "active" : ""
                }`}
              onMouseEnter={() => setActiveSubItem("Affiliate")}
              onMouseLeave={() => setActiveSubItem(null)}
            >
              <Link
                to="/Event#affiliate"
                className="dropdown-link"
                onClick={(e) => handleDropdownLinkClick("#affiliate", e)}
              >
                {translations.affiliate}
                {activeSubItem === "Affiliate" && (
                  <FaArrowRight className="right-arrow" />
                )}
              </Link>

              {activeSubItem === "Affiliate" && (
                <div className="dropdown-description affiliate0">
                  <p className="description-title">{translations.affiliate}</p>
                  <p className="description-subtitle">
                    {translations.affiliateDesc}
                  </p>
                </div>
              )}
            </div>

            {/* FAQ Link */}
            <div
              className={`dropdown-item dropdown-item-faq ${activeSubItem === "FAQ" ? "active" : ""
                }`}
              onMouseEnter={() => setActiveSubItem("FAQ")}
              onMouseLeave={() => setActiveSubItem(null)}
            >
              <Link
                to="/Event#faq"
                className="dropdown-link"
                onClick={(e) => handleDropdownLinkClick("#faq", e)}
              >
                {translations.faq}
                {activeSubItem === "FAQ" && (
                  <FaArrowRight className="right-arrow" />
                )}
              </Link>

              {activeSubItem === "FAQ" && (
                <div className="dropdown-description faqs">
                  <p className="description-title">{translations.faq}</p>
                  <p className="description-subtitle">
                    {translations.faqDesc}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="nav-item-wrapper">
        <Nav.Link
          as={Link}
          to="/relive"
          className="nav-link nav-link-relive"
          onClick={() => {
            setActiveDropdown(null);
            setIsNavOpen(false);
            handleMainNavLinkClick();

            const getUTM = () => {
              const stored = sessionStorage.getItem('utm_params');
              return stored ? JSON.parse(stored) : {};
            };
            const utm = getUTM();

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              'event': 'action_cta_click',
              'button_text': 'Relive',
              'class_id': 'nav-link nav-link-relive',
              'position': {
                'page': 'home',
                'nav_bar': 'header',
                'sub_nav_bar': 'main-nav'
              },
              'referrer_source': document.referrer,
              'utm_source': utm.utm_source || '',
              'utm_medium': utm.utm_medium || '',
              'utm_campaign': utm.utm_campaign || '',
              'utm_term': utm.utm_term || '',
              'utm_content': utm.utm_content || '',
              'gclid': utm.gclid || '',
              'fbclid': utm.fbclid || '',
              'ttclid': utm.ttclid || ''
            });
          }}
        >
          Relive
        </Nav.Link>
      </div>

      {/* About Us Link */}
      <div
        className={`nav-item-wrapper ${activeDropdown === "About Us" ? "active" : ""
          }`}
        onMouseEnter={() => handleMouseEnter("About Us")}
        onMouseLeave={handleMouseLeave}
      >
        <Nav.Link
          as={Link}
          to="/about"
          className={`nav-link nav-link-about ${activeDropdown === "About Us" ? "active-link" : ""
            }`}
          onClick={(event) => {
            const getUTM = () => {
              const stored = sessionStorage.getItem('utm_params');
              return stored ? JSON.parse(stored) : {};
            };
            const utm = getUTM();

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              'event': 'action_cta_click',
              'button_text': 'About Us',
              'class_id': 'nav-link nav-link-about',
              'position': {
                'page': 'home',
                'nav_bar': 'header',
                'sub_nav_bar': 'main-nav'
              },
              'referrer_source': document.referrer,
              'utm_source': utm.utm_source || '',
              'utm_medium': utm.utm_medium || '',
              'utm_campaign': utm.utm_campaign || '',
              'utm_term': utm.utm_term || '',
              'utm_content': utm.utm_content || '',
              'gclid': utm.gclid || '',
              'fbclid': utm.fbclid || '',
              'ttclid': utm.ttclid || ''
            });

            if (isMobile) {
              const clickedToggle = event.target.closest(".dropdown-icon");
              if (clickedToggle) {
                toggleDropdown(event, "About Us");
                return;
              }
              handleDropdownLinkClick("#about-us", event);
            } else {
              toggleDropdown(event, "About Us");
              handleMainNavLinkClick();
            }
          }}
        >
          <span className="nav-link-label">{translations.aboutUs}</span>
          <span className="dropdown-icon">
            <FaChevronDown />
          </span>
        </Nav.Link>
        {activeDropdown === "About Us" && (
          <div className="dropdown-about">
            <div
              className={`dropdown-item ${activeSubItem === "About Us" ? "active" : ""
                }`}
              onMouseEnter={() => setActiveSubItem("About Us")}
              onMouseLeave={() => setActiveSubItem(null)}
            >
              <Link
                to={isMobile ? "/about" : "/about#about-us"}
                className="dropdown-link dropdown-link-about-us"
                onClick={(e) => handleDropdownLinkClick("#about-us", e)}
              >
                {translations.aboutUs}
                {activeSubItem === "About Us" && (
                  isArabic ? (
                    <FaArrowLeft className="left-arrow" />
                  ) : (
                    <FaArrowRight className="right-arrow" />
                  )
                )}
              </Link>

              {activeSubItem === "About Us" && (
                <div className="dropdown-description ab">
                  <p className="description-title">{translations.aboutUs}</p>
                  <p className="description-subtitle">
                    {translations.aboutUsDesc}
                  </p>
                </div>
              )}
            </div>

            <div
              className={`dropdown-item ${activeSubItem === "Freak Squad" ? "active" : ""
                }`}
              onMouseEnter={() => setActiveSubItem("Freak Squad")}
              onMouseLeave={() => setActiveSubItem(null)}
            >
              <Link
                to="/about#freak-squad"
                className="dropdown-link dropdown-link-freak-squad"
                onClick={(e) => handleDropdownLinkClick("#freak-squad", e)}
              >
                {translations.freakSquad}
                {activeSubItem === "Freak Squad" && (
                  isArabic ? (
                    <FaArrowLeft className="left-arrow" />
                  ) : (
                    <FaArrowRight className="right-arrow" />
                  )
                )}
              </Link>

              {activeSubItem === "Freak Squad" && (
                <div className="dropdown-description freak-squad">
                  <p className="description-title">{translations.freakSquad}</p>
                  <p className="description-subtitle">
                    {translations.freakSquadDesc}
                  </p>
                </div>
              )}
            </div>

            <div
              className={`dropdown-item ${activeSubItem === "Timeline" ? "active" : ""
                }`}
              onMouseEnter={() => setActiveSubItem("Timeline")}
              onMouseLeave={() => setActiveSubItem(null)}
            >
              <Link
                to="/about#timeline"
                className="dropdown-link dropdown-link-timeline"
                onClick={(e) => handleDropdownLinkClick("#timeline", e)}
              >
                {translations.timeline}
                {activeSubItem === "Timeline" && (
                  isArabic ? (
                    <FaArrowLeft className="left-arrow" />
                  ) : (
                    <FaArrowRight className="right-arrow" />
                  )
                )}
              </Link>

              {activeSubItem === "Timeline" && (
                <div className="dropdown-description timeline">
                  <p className="description-title">{translations.timeline}</p>
                  <p className="description-subtitle">
                    {translations.timelineDesc}
                  </p>
                </div>
              )}
            </div>

            <div
              className={`dropdown-item dropdown-item-partner-with-us ${activeSubItem === "Partner With Us" ? "active" : ""
                }`}
              onMouseEnter={() => setActiveSubItem("Partner With Us")}
              onMouseLeave={() => setActiveSubItem(null)}
            >
              <Link
                to="mailto:Commercial@freaksofnature.me"
                className="dropdown-link dropdown-link-partner-with-us"
                onClick={() => setActiveDropdown(null)}
              >
                {translations.partnerWithUs}
                {activeSubItem === "Partner With Us" && (
                  isArabic ? (
                    <FaArrowLeft className="left-arrow" />
                  ) : (
                    <FaArrowRight className="right-arrow" />
                  )
                )}
              </Link>

              {activeSubItem === "Partner With Us" && (
                <div className="dropdown-description partner">
                  <p className="description-title">{translations.partnerWithUs}</p>
                  <p className="description-subtitle">
                    {translations.partnerWithUsDesc}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Blog Link */}
      <div className="nav-item-wrapper">
        <Nav.Link
          as={Link}
          to="/blog"
          className="nav-link nav-link-blog"
          onClick={() => {
            setActiveDropdown(null);
            setIsNavOpen(false);
            handleMainNavLinkClick();

            const getUTM = () => {
              const stored = sessionStorage.getItem('utm_params');
              return stored ? JSON.parse(stored) : {};
            };
            const utm = getUTM();

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              'event': 'action_cta_click',
              'button_text': 'Blog',
              'class_id': 'nav-link nav-link-blog',
              'position': {
                'page': 'home',
                'nav_bar': 'header',
                'sub_nav_bar': 'main-nav'
              },
              'referrer_source': document.referrer,
              'utm_source': utm.utm_source || '',
              'utm_medium': utm.utm_medium || '',
              'utm_campaign': utm.utm_campaign || '',
              'utm_term': utm.utm_term || '',
              'utm_content': utm.utm_content || '',
              'gclid': utm.gclid || '',
              'fbclid': utm.fbclid || '',
              'ttclid': utm.ttclid || ''
            });
          }}
        >
          {translations.blog}
        </Nav.Link>
      </div>
    </>
  );

  return (
    <>
      <Navbar expand="lg" className="navbar-custom sticky-top">
        <Navbar.Brand as={Link} to="/" onClick={handleLogoClick}>
          <img src={logo} alt="Freaks of Nature" className="navbar-logo" />
        </Navbar.Brand>
        <Navbar.Toggle aria-controls="basic-navbar-nav" onClick={toggleNav}>
          {isNavOpen ? (
            <FaTimes className="close-icon" />
          ) : (
            <FaBars className="hamburger-icon" />
          )}
        </Navbar.Toggle>
        <Navbar.Collapse
          id="basic-navbar-nav"
          in={isNavOpen}
        >
          <Nav className="navbar-center">
            {navItems}
          </Nav>
          <div className="navbar-language-toggle">
            {location.pathname !== '/affiliate' && <LanguageToggle />}
          </div>
        </Navbar.Collapse>
        <div className="navbar-buttons">
          {location.pathname === '/affiliate' ? (
            <button
              className="b1 btn-cta-signup"
              type="button"
              onClick={(e) => {
                e.preventDefault();

                const position = {
                  'page': 'affiliate',
                  'nav_bar': 'header',
                  'sub_nav_bar': 'navbar'
                };

                window.dispatchEvent(new CustomEvent('openAffiliatePopup', {
                  detail: { position }
                }));
              }}
            >
              {isArabic ? "سجل الآن" : "Sign Up"}
            </button>
          ) : (
            <a
              href="https://nofomo.com/events/freaks-of-nature-e7-freak-yard"
              target="_blank"
              rel="noopener noreferrer"
              className="b1 btn-cta-join-event"
              type="button"
              onClick={(e) => {
                // e.preventDefault();
                const getUTM = () => {
                  const stored = sessionStorage.getItem('utm_params');
                  return stored ? JSON.parse(stored) : {};
                };
                const utm = getUTM();

                const getPageName = () => {
                  const path = location.pathname;
                  if (path === '/') return 'home';
                  if (path === '/about') return 'about';
                  if (path === '/blog') return 'blog';
                  if (path.startsWith('/blog/')) return 'blog';
                  if (path === '/Event') return 'event';
                  return path.replace('/', '') || 'home';
                };

                const position = {
                  'page': getPageName(),
                  'nav_bar': 'header',
                  'sub_nav_bar': 'navbar'
                };

                window.dataLayer = window.dataLayer || [];
                window.dataLayer.push({
                  'event': 'action_cta_click',
                  'button_text': 'Join Next Event',
                  'class_id': 'b1 btn-cta-join-event',
                  'position': position,
                  'referrer_source': document.referrer,
                  'utm_source': utm.utm_source || '',
                  'utm_medium': utm.utm_medium || '',
                  'utm_campaign': utm.utm_campaign || '',
                  'utm_term': utm.utm_term || '',
                  'utm_content': utm.utm_content || '',
                  'gclid': utm.gclid || '',
                  'fbclid': utm.fbclid || '',
                  'ttclid': utm.ttclid || ''
                });

                // window.dispatchEvent(new CustomEvent('openSignupPopup', {
                //   detail: { position }
                // }));
              }}
            >
              {translations.joinNextEvent}
            </a>
          )}

          <a
            className="b2 btn-cta-follow-us"
            href="https://www.instagram.com/werfreaksofnature/?follow"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              const getUTM = () => {
                const stored = sessionStorage.getItem('utm_params');
                return stored ? JSON.parse(stored) : {};
              };
              const utm = getUTM();

              window.dataLayer = window.dataLayer || [];
              window.dataLayer.push({
                'event': 'action_social_share_click',
                'platform': 'instagram',
                'content_title': 'Follow Us',
                'referrer_source': document.referrer,
                'utm_source': utm.utm_source || '',
                'utm_medium': utm.utm_medium || '',
                'utm_campaign': utm.utm_campaign || '',
                'utm_term': utm.utm_term || '',
                'utm_content': utm.utm_content || '',
                'gclid': utm.gclid || '',
                'fbclid': utm.fbclid || '',
                'ttclid': utm.ttclid || ''
              });
            }}
          >
            {translations.followUs}
          </a>
        </div>
      </Navbar>


      <style jsx>{`
        .navbar-custom {
          background: linear-gradient(360deg, #00000000, #000000);
          display: flex;
          align-items: center;
          justify-content: space-between;
          height: 124px;
          position: relative;
        }
        .dropdown-link-partner-with-us {
          text-decoration: none;
        }
        
        .navbar-custom .navbar-brand {
          flex-shrink: 0;
        }
        
        .navbar-custom .navbar-collapse {
          flex: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0;
        }
        
        .navbar-custom .navbar-buttons {
          flex-shrink: 0;
        }

        .sticky-top {
          position: sticky;
          top: 0;
          z-index: 1020;
        }

        .navbar-logo {
          width: 124px;
          height: auto;
          margin-left: 50px;
        }

        .nav-link {
          padding: 18px 24px !important;
        }

        .navbar-nav .nav-link:hover {
          color: #ffbf00 !important;
        }

        @media (min-width: 1141px) {
          .navbar-collapse {
            display: flex !important;
            flex-direction: row;
            align-items: center;
            justify-content: center;
          }
          
          .navbar-center {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-wrap: wrap;
            gap: 0;
            flex: 1;
          }
          
          .navbar-collapse .navbar-nav {
            display: flex;
            justify-content: center;
            align-items: center;
          }
          
          .navbar-language-toggle {
            flex-shrink: 0;
            margin-left: 20px;
            ${isArabic ? 'margin-left: 0; margin-right: 10px;' : ''}
          }
        }
        
        @media (min-width: 1700px) {
          .navbar-center {
            margin-left: 300px;
            ${isArabic ? 'margin-left: 0; margin-right: 300px;' : ''}
          }
        }
        
        @media (max-width: 1180px) {
          .navbar-center {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-wrap: wrap;
            gap: 0;
          }
        }

        .navbar-buttons {
          display: flex;
          align-items: center;
          gap: 20px;
          margin-right: 50px;
          ${isArabic ? 'margin-left: 20px; margin-right: 20px;' : ''}
        }

        .navbar-language-toggle {
          display: flex;
          align-items: center;
          margin-left: 20px;
          margin-right: 20px;
          ${isArabic ? 'margin-left: 0; margin-right: 10px;' : ''}
        }
        

        @media (max-width: 1400px) {
          .navbar-language-toggle {
            margin: 10px 0;
            justify-content: center;
          }
        }

        .navbar-nav .nav-link {
          color: #fff !important;
          font-size: 20px;
          font-weight: 500;
          margin: 0;
          transition: color 0.3s ease;
          text-transform: uppercase;
        }

        .b1 {
          background-color: #ed196f;
          border-radius: 10px;
          border: 1px solid #000;
          text-transform: uppercase;
          padding: 10px 20px;
          color: white;
          font-size: 20px;
          font-weight: bold;
          box-shadow: 5px 5px 0px 0px #000;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          text-decoration: none;
        }

        .b2 {
          background-color: #ffbf00;
          border-radius: 10px;
          border: 1px solid #000;
          text-transform: uppercase;
          padding: 10px 20px;
          font-size: 20px;
          font-weight: bold;
          box-shadow: 5px 5px 0px 0px #000;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #000;
          text-decoration: none;
          cursor: pointer;
        }

        .dropdown-freakyard {
          position: absolute;
          top: 100%;
          left: 0%;
          background-color: rgb(0 0 0 / 80%);
          border-radius: 0px 20px 20px 20px;
          padding: 10px 24px;
          width: 562px;
          height: 200px;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
          z-index: 1050;
        }

        .dropdown-relive {
          position: absolute;
          top: 100%;
          left: -176px;
          background-color: rgb(0 0 0 / 80%);
          border-radius: 20px;
          padding: 50px 40px;
          width: 562px;
          height: 250px;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
          z-index: 1050;
        }

        .dropdown-about {
          position: absolute;
          top: 100%;
          left: 0px;
          background-color: rgb(0 0 0 / 80%);
          border-radius: 0px 20px 20px 20px;
          padding: 10px 20px;
          width: 562px;
          height: 200px;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
          z-index: 1050;
          ${isArabic ? 'overflow: visible;' : ''}
          ${isArabic ? 'right: 0; left: auto;' : ''}
          ${isArabic ? 'border-radius: 20px 0px 20px 20px;' : ''}
          ${isArabic ? 'display: flex; flex-direction: column; align-items: flex-end;' : ''}
        }

        .dropdown-blog {
          position: absolute;
          top: 100%;
          right: 0px;
          background-color: rgb(0 0 0 / 80%);
          border-radius: 20px 0px 20px 20px;
          padding: 50px 40px;
          width: 562px;
          height: 250px;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
          z-index: 1050;
        }

        .dropdown-item {
          display: flex;
          align-items: center;
          padding: 10px 0px 4px;
          position: relative;
          cursor: pointer;
          width: 164px;
          border-bottom: 2px solid #464646;
          color: #464646 !important;
        }
        .dropdown-item-partner-with-us {
          border-bottom: 0px !important;
        }
        .dropdown-item-faq {
          border-bottom: 0px !important;
        }
        
        ${isArabic ? `
          .dropdown-about .dropdown-item {
            margin-left: auto;
            margin-right: 0;
          }
        ` : ''}

        .b {
          border-bottom: 0px !important;
        }

        .dropdown-link {
          color: #fff;
          font-size: 20px;
          font-weight: 500;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: relative;
        }
        
        ${isArabic ? `
          .dropdown-about .dropdown-link {
            flex-direction: row-reverse;
            justify-content: flex-end;
          }
        ` : ''}

        .dropdown-link-relive {
          color: #fff;
          font-size: 27.45px;
          font-weight: bold;
          width: 134px;
          display: flex;
          flex-direction: column;
          position: relative;
        }

        .dropdown-link-relive p {
          font-size: 14px;
          font-weight: normal;
          margin-bottom: 0;
          line-height: 18px;
        }

        .right-arrow {
          margin-left: 10px;
          color: #fff;
          font-size: 14px;
        }

        .left-arrow {
          margin-right: 10px;
          color: #fff;
          font-size: 14px;
        }
        
        ${isArabic ? `
          .dropdown-about .left-arrow {
            margin-right: 10px;
            margin-left: 0;
            order: -1;
          }
        ` : ''}

        a {
          text-decoration: none;
        }

        .dropdown-description {
          display: flex;
          flex-direction: column;
          width: 273px;
          color: #fff;
          padding: 10px;
          animation: fadeIn 0.3s ease-in-out;
          position: absolute;
          left: 152%;
        }
        
        ${isArabic ? `
          .dropdown-about .dropdown-description {
            left: auto;
            right: 152%;
            text-align: right;
          }
        ` : ''}

        .day {
          top: 110%;
        }
        .week {
          top: 8%;
        }
        .lineup {
          top: 42%;
        }
        .tickets {
          top: -60%;
        }
        .affiliate0 {
          top: -158%;
        }
        .faqs {
          top: -264%;
        }

        .ab {
          top: 42%;
        }
        .freak-squad {
          top: -50%;
        }
        .timeline {
          top: -158%;
        }
        .partner {
          top: -264%;
        }
        .djcomp1 {
          top: -372%;
        }
        .affiliate01 {
          top: -487%;
        }

        .description-title {
          font-size: 27.45px;
          font-weight: bold;
          margin-bottom: 5px;
        }

        .description-subtitle {
          font-size: 14px;
          font-weight: normal;
          margin-bottom: 0;
          line-height: 16px;
          text-wrap: wrap;
        }

        .nav-item-wrapper {
          position: relative;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }

        .nav-item-wrapper.active .nav-link {
          background-color: rgb(0 0 0 / 80%);
          color: #ffbf00;
          border-radius: 20px 20px 0 0;
          z-index: 1000;
          transition: all 0.3s ease-in-out;
        }

        /* Animations */
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 1400px) {
          .sticky-top {
            position: sticky;
            top: 54px;
            z-index: 1020;
          }

          .navbar-toggler-icon {
            background-color: white;
          }

          .navbar-collapse {
            justify-content: flex-start;
            flex-basis: 0;
            flex-grow: 0;
            position: absolute;
            top: 0px;
            left: 0;
            width: 100%;
            background-color: #000;
            padding: 130px 40px 40px;
            border-radius: 0 0 20px 20px;
            transition: none !important;
          }
          
          .navbar-collapse.collapsing {
            transition: none !important;
          }
          
          .navbar-collapse:not(.show) {
            opacity: 0;
            transform: none;
          }
          
          .navbar-collapse.show {
            opacity: 1;
            transform: none;
          }

          .navbar-expand-lg .navbar-toggler {
            display: block !important;
          }

          .navbar-expand-lg .navbar-collapse {
            display: none !important;
          }

          .navbar-expand-lg .navbar-collapse.show {
            display: block !important;
          }

          .navbar-toggler {
            order: -1;
            margin-left: 10px;
            border: none;
            position: relative;
            z-index: 1;
          }

          .navbar-nav {
            align-items: flex-start;
            width: 100%;
          }

          .navbar-custom {
            padding: 0px;
          }

          .navbar-center {
            width: 100%;
            justify-content: center;
            align-items: flex-start;
          }
          
          .navbar-collapse .navbar-nav {
            width: 100%;
            justify-content: flex-start;
          }

          .b2 {
            display: none;
          }

          .navbar-logo {
            display: none;
          }

          .navbar-buttons {
            margin-right: 20px;
            margin-left: auto;
            position: relative;
            z-index: 1;
            justify-content: flex-end;
            gap: 10px;
          }

          .hamburger-icon,
          .close-icon {
            font-size: 30px;
            color: white;
            position: relative;
            z-index: 1;
          }

          .hamburger-icon:hover,
          .close-icon:hover {
            color: #ffbf00;
          }

          .nav-item-wrapper {
            position: relative;
            width: 100%;
          }

          .navbar-center .dropdown-icon {
            display: block;
          }

          .nav-link {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 0 !important;
          }

          .navbar-nav .nav-link {
            font-size: 24px;
            border-bottom: 2px solid #ffffff38;
            width: 100%;
            line-height: 50px;
            cursor: pointer;
          }

          .dropdown-freakyard,
          .dropdown-relive,
          .dropdown-about,
          .dropdown-blog {
            position: static !important;
            width: 100% !important;
            height: auto !important;
            border-radius: 0 !important;
            padding: 0 !important;
            margin: 0 !important;
            background-color: transparent !important;
            box-shadow: none !important;
            left: auto !important;
            top: auto !important;
          }
          .nav-item-wrapper {
            flex-direction: column;
          }
          .dropdown-item {
            width: 100%;
            padding: 10px 0 10px 20px;
            border-bottom: 1px solid #464646;
          }

          .dropdown-link {
            font-size: 18px;
            color: #ccc;
          }

          .dropdown-link-relive {
            font-size: 18px;
            width: 100%;
            padding-left: 20px;
          }

          .dropdown-link-relive p {
            font-size: 14px;
            color: #ccc;
          }

          .dropdown-description {
            display: none;
          }

          .right-arrow {
            display: none;
          }

          .left-arrow {
            display: none;
          }

          .nav-item-wrapper.active .nav-link {
            border-radius: 0;
            background-color: transparent;
          }

          .dropdown-icon {
            transition: transform 0.3s ease;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
          }

          .nav-item-wrapper.active .dropdown-icon {
            transform: rotate(180deg);
          }
        }

        @media (max-width: 468px) {
          .sticky-top {
            top: 40px;
          }

          .navbar-logo {
            width: 91px;
          }

          .navbar-custom {
            padding: 0px 5px;
          }

          .navbar-toggler {
            margin-left: 0px;
          }

          .b1 {
            padding: 5px 12px;
            font-size: 14px;
            border-radius: 5px;
            box-shadow: 2.46px 2.46px 0px 0px #000;
          }

          .navbar-collapse {
            padding: 130px 20px 30px;
          }

          .dropdown-description {
            display: none;
          }
        }
${isArabic ? `
  .dropdown-freakyard {
    right: 0;
    left: auto;
    border-radius: 20px 0px 20px 20px;
  }
  
  .dropdown-freakyard .dropdown-item {
    margin-left: auto;
    margin-right: 0;
  }
  
  .dropdown-freakyard .dropdown-link {
    flex-direction: row-reverse;
    justify-content: flex-end;
  }
  
  .dropdown-freakyard .dropdown-description {
    left: auto;
    right: 152%;
    text-align: right;
  }
  
  .dropdown-freakyard .left-arrow {
    margin-right: 10px;
    margin-left: 0;
    order: -1;
  }
` : ''}
      `}</style>
    </>
  );
}

export default Header;